import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/UI";
import { CaretDown, Trophy, User } from "@phosphor-icons/react";
import { GroupedTable, toggleWithScrollPreserve } from "@/components/VetTable";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const OUTCOMES = [
  { key: "tedavi görüyor", label: "Aktif", bg: "#FBF1DE", fg: "#8B6914", dot: "#DDA74F" },
  { key: "iyileşti", label: "Tamamlanan", bg: "#E4EEE6", fg: "#2D4A36", dot: "#2D6A4F" },
  { key: "vefat", label: "Vefat", bg: "#FBECEA", fg: "#8B2E1D", dot: "#C45B45" },
  { key: "iptal", label: "İptal", bg: "#EEECE5", fg: "#5e675e", dot: "#8a938c" },
];

// Tedavi kayıtlarını Yıl → Ay kartları halinde gruplar (tohumlama modülüyle aynı desen).
// Kart başlığı o ayın TAM dağılımını (tüm durumlar) gösterir; statusTab yalnızca
// akordiyon içindeki detay listesini ve hangi ayların görüneceğini filtreler.
export function TreatmentMonthlyCards({ items, statusTab, columns, renderExpanded, dayMeta, emptyText }) {
  const [openKey, setOpenKey] = useState(null);
  const didInit = useRef(false);

  const years = useMemo(() => {
    const byMonth = {};
    for (const x of items) {
      const d = (x.date || "").slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(d)) continue;
      if (!byMonth[d]) byMonth[d] = { key: d, records: [], dist: { "tedavi görüyor": 0, "iyileşti": 0, "vefat": 0, "iptal": 0 }, meds: {}, vets: {} };
      const m = byMonth[d];
      m.records.push(x);
      const oc = x.outcome || "tedavi görüyor";
      if (m.dist[oc] !== undefined) m.dist[oc] += 1;
      // İlaç kullanım adedi: o ilacı İÇEREN tedavi kaydı sayısı (paket ilaçları ayrı ayrı).
      const names = x.is_package
        ? (x.medicines || []).map(mm => mm.medicine_name).filter(Boolean)
        : (x.medicine_name ? [x.medicine_name] : []);
      for (const nm of [...new Set(names)]) m.meds[nm] = (m.meds[nm] || 0) + 1;
      const vet = (x.vet_name || "").trim();
      if (vet) m.vets[vet] = (m.vets[vet] || 0) + 1;
    }
    for (const k in byMonth) {
      const m = byMonth[k];
      m.total = m.records.length;
      m.topMeds = Object.entries(m.meds).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([name, count]) => ({ name, count }));
      const vEntries = Object.entries(m.vets);
      const maxV = Math.max(0, ...vEntries.map(e => e[1]));
      m.topVets = vEntries
        .filter(e => e[1] === maxV && maxV > 0)
        .sort((a, b) => a[0].localeCompare(b[0], "tr"))
        .map(([name, count]) => ({ name, count }));
      m.records.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      m.filtered = statusTab && statusTab !== "all"
        ? m.records.filter(r => (r.outcome || "tedavi görüyor") === statusTab)
        : m.records;
    }
    const byYear = {};
    Object.values(byMonth).filter(m => m.filtered.length > 0).forEach(m => {
      const y = m.key.slice(0, 4);
      (byYear[y] = byYear[y] || []).push(m);
    });
    return Object.entries(byYear)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([year, months]) => ({
        year,
        months: months.sort((a, b) => b.key.localeCompare(a.key)),
        total: months.reduce((s, m) => s + m.filtered.length, 0),
      }));
  }, [items, statusTab]);

  // İlk veri geldiğinde en güncel görünür ayı aç
  useEffect(() => {
    if (!didInit.current && years.length) {
      setOpenKey(years[0].months[0].key);
      didInit.current = true;
    }
  }, [years]);

  // Sekme değişince en güncel görünür ayı aç (ilk yüklemede didInit false → atlar)
  useEffect(() => {
    if (didInit.current) setOpenKey(years[0]?.months[0]?.key || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab]);

  if (!years.length) return <Card className="text-center text-[#8a938c] py-10" testId="treat-monthly-empty">{emptyText || "Bu durumda tedavi kaydı yok."}</Card>;

  return (
    <div className="space-y-5" data-testid="treat-monthly-cards">
      {years.map(({ year, months, total }) => (
        <div key={year}>
          <div className="flex items-center gap-3 mb-2.5">
            <h3 className="heading text-lg text-[#1f3326]" data-testid={`treat-year-${year}`}>{year}</h3>
            <span className="text-xs text-[#8a938c]">{total} tedavi · {months.length} ay</span>
            <div className="flex-1 h-px bg-[#E5E2DC]" />
          </div>
          <div className="space-y-2.5">
            {months.map(m => {
              const label = `${TR_MONTHS[Number(m.key.slice(5, 7)) - 1]} ${year}`;
              const open = openKey === m.key;
              return (
                <Card key={m.key} className="!p-0 overflow-hidden" testId={`treat-month-${m.key}`}>
                  <button type="button" data-testid={`treat-month-toggle-${m.key}`}
                    onClick={(e) => toggleWithScrollPreserve(e, m.key, setOpenKey)}
                    className="w-full text-left px-4 py-3.5 hover:bg-[#F7F6F3] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-[#1f3326] text-base">{label}</span>
                          <span className="tabnum text-sm font-bold text-white bg-[#2D4A36] px-2.5 py-0.5 rounded-full" data-testid={`treat-month-total-${m.key}`}>{m.total} tedavi</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs" data-testid={`treat-month-dist-${m.key}`}>
                          {OUTCOMES.map(o => (
                            <span key={o.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-md font-medium" style={{ background: o.bg, color: o.fg }}>
                              <span className="w-2 h-2 rounded-sm" style={{ background: o.dot }} />{o.label}: <b className="tabnum">{m.dist[o.key]}</b>
                            </span>
                          ))}
                        </div>
                        {m.topMeds.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs text-[#5e675e]" data-testid={`treat-month-topmeds-${m.key}`}>
                            <Trophy size={13} weight="fill" className="text-[#C9A227]" />
                            {m.topMeds.map((md, i) => (
                              <span key={md.name} className="px-2 py-0.5 rounded-full bg-[#FAF8F3] border border-[#ECE8E0]">
                                {i === 0 ? "🥇 " : ""}{md.name} <b className="tabnum">({md.count})</b>
                              </span>
                            ))}
                          </div>
                        )}
                        {m.topVets.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs" data-testid={`treat-month-topvet-${m.key}`}>
                            <User size={13} weight="fill" className="text-[#2D4A36]" />
                            {m.topVets.map(v => (
                              <span key={v.name} className="px-2 py-0.5 rounded-full bg-[#EEF3EF] border border-[#DCE6DE] font-medium text-[#2D4A36]">
                                {v.name} <b className="tabnum">({v.count})</b>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <CaretDown size={18} weight="bold" className={`text-[#8a938c] mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-[#ECE8E0]" data-testid={`treat-month-detail-${m.key}`}>
                      <GroupedTable testId={`treat-m-${m.key}`} items={m.filtered} pageSize={1000}
                        emptyText="Kayıt yok" dayMeta={dayMeta} renderExpanded={renderExpanded}
                        columns={columns} className="!border-0 !rounded-none" />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
